import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Form } from "react-bootstrap";
import api from "../../api";
import ButtonLoader from "components/Common/ButtonLoader";
import { Link, useHistory, useParams } from "react-router-dom";
import Select from "react-select";
import {
  checkPermissionStatus,
  generateMaskedPII,
  getRoleName,
  getSubdomain,
} from "helpers/utils";
import { isEmpty, lowerCase } from "lodash";
import arrow_btn from "../../assets/images/icon/arrow-btn.svg";
import TimeslotBooking from "./TimeslotBooking";
import CalendarPreview from "./CalendarPreview";
import { decryptDataFromArray } from "helpers/EncDec";
import moment from "moment";
import { toast } from "react-toastify";
import { getUserInfo, getUserToken } from "helpers/authHelper";
import { MultiSelect } from "react-multi-select-component";
import { useTranslation } from "react-i18next";
import { phoneValidationRules } from "helpers/common";

const styles = {
  option: (styles, state) => ({
    ...styles,
    backgroundColor: state.isSelected ? "#E27235" : "",
  }),
};

const timeslots = {
  Monday: [],
  Tuesday: [],
  Wednesday: [],
  Thursday: [],
  Friday: [],
  Saturday: [],
  Sunday: [],
};

const EditUser = props => {
  const { t } = useTranslation();
  const history = useHistory();
  let { userId } = useParams();
  const userInfo = getUserInfo();
  const isSelfUser = parseInt(userInfo?.id) === parseInt(userId);
  const isAdminUser = checkPermissionStatus("user-list", "edit");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [statePayload, setStatePayload] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [timeSlotsData, settimeSlotsData] = useState({});
  const [ErrorsTimeslots, setErrorsTimeslots] = useState({}); // State to track validation errors
  const [isVendorVisible, setIsVendorVisible] = useState(false);
  const [vendor, setVendor] = useState([]);
  const [isUserLoaded, setIsUserLoaded] = useState(false);
  const handleErrorsValueChange = errors => {
    setErrorsTimeslots(errors);
  };
  const roleName = getRoleName();

  const [CountryData, setCountryData] = useState([]);
  const [RegionData, setRegionData] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [StateData, setStateData] = useState([]);
  const [selectedStates, setSelectedStates] = useState([]);
  const [CityData, setCityData] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [LocationData, setLocationData] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [FranchiseData, setFranchiseData] = useState([]);
  const [selectedFranchises, setSelectedFranchises] = useState([]);

  // Day to integer map
  const dayToIntegerMap = {
    Sunday: 0,
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
  };

  // Integer to day map
  const integerToDayMap = {
    0: "Sunday",
    1: "Monday",
    2: "Tuesday",
    3: "Wednesday",
    4: "Thursday",
    5: "Friday",
    6: "Saturday",
  };

  const [daytimeSlots, setDaytimeSlots] = useState("");

  // Function to update the state in the parent component
  const handleChildValueChange = value => {
    // Parse the JSON string into an object
    const jsonObject = JSON.parse(value);
    setDaytimeSlots(jsonObject);
  };

  const handleImageFileChange = e => {
    setImageFile(e.target.files[0]);
  };

  const handleInput = event => {
    const { name, value } = event.target;

    // For phone field, only allow digits
    if (name === "phone") {
      const digitsOnly = value.replace(/\D/g, "");
      setStatePayload(prevState => ({
        ...prevState,
        [name]: digitsOnly,
      }));
      return;
    }

    setStatePayload(prevState => ({
      ...prevState,
      [name]: value,
    }));
  };

  // Get country label by country ID
  const getCountryLabel = countryId => {
    const country = CountryData?.find(
      c => c.id === countryId || c.value === countryId
    );
    return country?.label || null;
  };

  // Validate phone number based on selected country
  const validatePhoneByCountry = (phone, countryId) => {
    if (!phone) {
      return { isValid: false, message: t(`Edit User.Validationmsg`) };
    }

    const countryLabel = getCountryLabel(countryId);
    const rule =
      phoneValidationRules[countryLabel] || phoneValidationRules.default;

    if (!rule.pattern.test(phone)) {
      return { isValid: false, message: rule.message };
    }

    return { isValid: true, message: "" };
  };

  // Get phone max length based on country
  const getPhoneMaxLength = countryId => {
    const countryLabel = getCountryLabel(countryId);
    const rule =
      phoneValidationRules[countryLabel] || phoneValidationRules.default;
    return rule.maxLength;
  };

  // Get phone placeholder based on country
  const getPhonePlaceholder = countryId => {
    const countryLabel = getCountryLabel(countryId);
    const placeholders = {
      India: "e.g., 9876543210",
      "United States": "e.g., 1234567890",
      "United States of America": "e.g., 1234567890",
      USA: "e.g., 1234567890",
      "United Kingdom": "e.g., 7911123456",
      UK: "e.g., 7911123456",
      "United Arab Emirates": "e.g., 501234567",
      UAE: "e.g., 501234567",
      "Saudi Arabia": "e.g., 512345678",
      Canada: "e.g., 4161234567",
      Australia: "e.g., 412345678",
      Germany: "e.g., 15123456789",
      France: "e.g., 612345678",
    };
    return placeholders[countryLabel] || "Enter mobile number";
  };

  const handleRoleSelection = roleId => {
    setStatePayload(prevState => ({
      ...prevState,
      role_id: roleId,
    }));
  };

  const populateValue = () => {};

  const handleSelectChange = (value, key) => {
    setStatePayload(prevState => ({
      ...prevState,
      [key]: value?.value,
    }));
  };

  const fetchSelectValue = item => {
    if (!item) return [];

    return [
      {
        label: item,
        value: item,
      },
    ];
  };

  const fetchVendorList = () => {
    api
      .get(`${process.env.REACT_APP_API_URL}/admin/vendor/vendorList`)
      .then(response => {
        let responseData = response?.data;
        responseData = decryptDataFromArray(responseData);

        const { success, allVendor } = responseData || {};

        if (success) {
          let data = allVendor?.map(item => ({
            id: item?.id,
            label: item?.vendor_name,
            value: item?.id,
          }));
          setVendor(data);
        }
      })
      .catch(err => {});
  };

  const updateUser = payload => {
    const updateEndpoint = isAdminUser
      ? `/admin/user/update-user/${userId}`
      : `/admin/user/update-profile/${userId}`;

    api
      .patch(`${process.env.REACT_APP_API_URL}${updateEndpoint}`, payload)
      .then(response => {
        setIsLoading(false);
        // if updating current user profile, update the local storage
            toast.success("User updated successfully");

        if (userId == userInfo?.id) {
          let updatedUser = JSON.parse(localStorage.getItem("user_info"));
          updatedUser = {
            ...updatedUser,
            name: `${statePayload?.firstname} ${statePayload?.lastname}`,
            email: statePayload?.email,
            phone: statePayload?.phone,
            role_id: statePayload?.role_id,
            company_id: statePayload?.company_id,
            country_id: statePayload?.country_id,
            city_ids: statePayload?.city_ids,
            state_ids: statePayload?.state_ids,
            region_ids: statePayload?.region_ids,
          };
          localStorage.setItem("user_info", JSON.stringify(updatedUser));
        }
        if (isAdminUser) {
          history.push("/user-list");
        } else {
          history.push(`/edit-user/${userId}`);
        }
      })
      .catch(err => {
        console.log(err);
        setIsLoading(false);
        const errorData = err.response?.data;

        if (errorData?.message?.errors) {
          // Extract all error messages dynamically
          const errorMessages = Object.values(errorData.message.errors).join(
            ", "
          );
          toast.error(errorMessages);
        } else {
          // Fallback to a general error message
          toast.error(
            errorData?.message || "Something went wrong. Please try again."
          );
        }
      });
  };

  const cleanArray = arr =>
    (arr || []).filter(id => id !== null && id !== undefined && id !== "");

  const handleSubmit = event => {
    event.preventDefault();
    setErrors({});
    setIsLoading(true);
    let errorObj = {};
    const errorMessages = [];

    // Catching and setting errors;
    let {
      password,
      confirm_password,
      change_password,
      company_id,
      country_id,
      vendor_id,
    } = statePayload;
    if (!statePayload?.firstname) {
      errorObj["firstname"] = t(`Edit User.Validationmsg`);
    }
    if (!statePayload?.lastname) {
      errorObj["lastname"] = t(`Edit User.Validationmsg`);
    }
    if (!statePayload?.email) {
      errorObj["email"] = t(`Edit User.Validationmsg`);
    }
    // Validate phone based on selected country
    const phoneValidation = validatePhoneByCountry(
      statePayload?.phone,
      statePayload?.country_id
    );
    if (!phoneValidation.isValid) {
      errorObj["phone"] = phoneValidation.message;
    }
    if (
      change_password &&
      password?.length >= 6 &&
      password !== confirm_password
    ) {
      errorObj["password"] = t(`Edit User.Passwords don't match!`);
      errorObj["confirm_password"] = t(`Edit User.Passwords don't match!`);
    }

    if (password?.length < 6) {
      errorObj["password"] = t(
        `Edit User.Password must be at least 6 characters!`
      );
    }

    if (!country_id) {
      errorObj["country_id"] = t(`Edit User.Please select a country`);
    }

    if (isVendorVisible) {
      if (vendor_id == undefined || vendor_id == null || vendor_id == 0) {
        errorObj["vendor_id"] = t(`Create User.Please select a Vendor`);
      }
    }

    if (Object.keys(errorObj)?.length > 0) {
      setIsLoading(false);
      setErrors(prevState => ({
        ...prevState,
        ...errorObj,
      }));

      return false;
    }

    let payload = new FormData();
    payload.append("firstname", statePayload?.firstname || "");
    payload.append("lastname", statePayload?.lastname || "");
    payload.append("email", statePayload?.email);
    payload.append("phone", statePayload?.phone);
    payload.append("username", statePayload?.username);
    payload.append("password", statePayload?.password);
    payload.append("change_password", statePayload?.change_password);

    let selectedRole = roles?.find(item => item?.label === statePayload?.role);
    payload.append("role_id", selectedRole?.id);

    payload.append("company_id", statePayload?.company_id);
    payload.append("country_id", statePayload?.country_id);
    payload.append("region_ids", statePayload?.region_ids?.join(","));
    payload.append("state_ids", statePayload?.state_ids?.join(","));
    payload.append("city_ids", statePayload?.city_ids?.join(","));
    payload.append("location_ids", statePayload?.location_ids?.join(","));
    payload.append(
      "franchise_ids",
      cleanArray(statePayload?.franchise_ids).join(",")
    );
    payload.append("vendorId", vendor_id);
    payload.append("vendor_visible", isVendorVisible);
    if (imageFile) {
      payload.append("image", imageFile);
    }

    updateUser(payload);
  };

  const fetchRoleData = () => {
    api
      .get(`${process.env.REACT_APP_API_URL}/admin/role/fetch-role-list`, {})
      .then(response => {
        const { success, allRoles } = response.data;
        if (success) {
          const { allRoles } = response.data;
          let data = allRoles?.map(item => ({
            id: item?.id,
            label: item?.name,
            value: item?.name,
          }));
          setRoles(data);
        }
      })
      .catch(err => console.log("Error:::", err));
  };
  const fetchUserDetails = () => {
    const endpoint = isAdminUser
      ? `/admin/user/fetch-userbyid/${userId}`
      : `/admin/user/fetch-profile/${userId}`;

    api
      .get(`${process.env.REACT_APP_API_URL}${endpoint}`)
      .then(response => {
        const success = response?.data?.success;
        const user = decryptDataFromArray(response?.data?.user);

        if (success) {
          const userSlots = JSON.parse(user.timeSlotsData);

          Object.keys(userSlots).forEach(key => {
            const value = userSlots[key];
            timeslots[integerToDayMap[key]] = value;
          });

          setStatePayload(user);
          setIsVendorVisible(user?.isvendor_visible);
          setSelectedRegions(user?.userRegions || []);
          setSelectedStates(user?.userStates || []);
          setSelectedCities(user?.userCities || []);
          setSelectedLocations(user?.userLocations || []);
          setSelectedFranchises(user?.userFranchises || []);

          const select = document.querySelector("select");
          const options = Array.from(select.options);

          let optionToSelect = options?.find(
            item => parseInt(item.value) === user?.role_id
          );

          optionToSelect?.setAttribute("selected", "selected");

          setIsUserLoaded(true);
        }
      })
      .catch(err => console.log("Error:::", err));
  };

  const fetchCountryData = () => {
    api
      .get(`${process.env.REACT_APP_API_URL}/admin/location/country-list`)
      .then(response => {
        const { success, allCountry } = response.data;
        if (success) {
          const { allCountry } = response.data;
          let data = allCountry?.map(item => ({
            id: item?.id,
            label: item?.name,
            value: item?.id,
          }));
          setCountryData(data);
        }
      })
      .catch(err => {});
  };
  const fetchRegionData = (company_id, franchiseIds) => {
    console.log(franchiseIds, "franchiseIds");
    api({
      method: "get",
      data: "",
      url: `${process.env.REACT_APP_API_URL}/admin/location/region-list`,
      params: {
        franchise_id: franchiseIds?.length
          ? franchiseIds.join(",") // "11,12,13"
          : null,
      },
    })
      .then(res => {
        let response = decryptDataFromArray(res?.data);
        const allRegion = response?.data;
        let data = allRegion?.map(item => ({
          id: item?.id,
          label: item?.region,
          value: item?.id,
          country_id: item?.country_id,
        }));
        setRegionData(data);
        setSelectedRegions(prev =>
          prev.filter(r => data.some(d => d.id === r.id))
        );
        setStatePayload(prev => ({
          ...prev,
          region_ids: (prev.region_ids || []).filter(id =>
            data.some(d => d.id === id)
          ),
        }));
      })
      .catch(err => {});
  };
  const fetchStateData = (company_id, franchiseIds) => {
    api({
      method: "get",
      data: "",
      url: `${process.env.REACT_APP_API_URL}/admin/location/state-list`,
      params: {
        franchise_id: franchiseIds?.length
          ? franchiseIds.join(",") // "11,12,13"
          : null,
      },
    })
      .then(res => {
        let response = decryptDataFromArray(res?.data);
        const StateList = response?.data;
        let data = StateList?.map(item => ({
          id: item?.id,
          label: item?.name,
          value: item?.id,
          country_id: item?.country_id,
          region_id: item?.region_id,
        }));
        setStateData(data);
        // clean previously selected states
        setSelectedStates(prevSelected =>
          prevSelected?.filter(selected =>
            data.some(state => state.value === selected.value)
          )
        );
      })
      .catch(err => {});
  };
  const fetchCityData = (company_id, franchiseIds) => {
    api
      .get(`${process.env.REACT_APP_API_URL}/admin/location/city-list`, {
        params: {
          franchise_id: franchiseIds?.length
            ? franchiseIds.join(",") // "11,12,13"
            : null,
        },
      })
      .then(res => {
        let response = decryptDataFromArray(res?.data);
        const CityList = response?.data;
        let data = CityList?.map(item => ({
          id: item?.id,
          label: item?.city,
          value: item?.id,
          country_id: item?.country_id,
          region_id: item?.region_id,
          state_id: item?.state_id,
        }));
        setCityData(data);
        // 🔥 IMPORTANT: clean previously selected cities
        setSelectedCities(prevSelected =>
          prevSelected?.filter(selected =>
            data.some(city => city.value === selected.value)
          )
        );
      })
      .catch(err => {});
  };
  const fetchLocationData = (company_id, franchiseIds) => {
    api({
      method: "get",
      data: "",
      url: `${process.env.REACT_APP_API_URL}/admin/location/location-list`,
      params: {
        franchise_id: franchiseIds?.length
          ? franchiseIds.join(",") // "11,12,13"
          : null,
      },
    })
      .then(res => {
        let response = decryptDataFromArray(res?.data);
        const locationList = response?.data;
        let data = locationList?.map(item => ({
          id: item?.id,
          label: item?.label,
          value: item?.value,
          country_id: item?.country_id,
          region_id: item?.region_id,
          state_id: item?.state_id,
          city_id: item?.city_id,
        }));
        setLocationData(data);
        // clean previously selected locations
        setSelectedLocations(prevSelected =>
          prevSelected?.filter(selected =>
            data.some(location => location.value === selected.value)
          )
        );
      })
      .catch(err => {});
  };

  const fetchFranchiseData = company_id => {
    api({
      method: "get",
      data: "",
      url: `${process.env.REACT_APP_API_URL}/admin/Franchise/franchise-dropdown`,
    })
      .then(res => {
        let response = decryptDataFromArray(res?.data);
        const franchiseList =
          response?.data?.filter(item => item.status === 1) || [];
        let data = franchiseList?.map(item => ({
          id: item?.id,
          label: item?.franchise_name,
          value: item?.id,
          region_ids: item?.region_ids || [],
          state_ids: item?.state_ids || [],
          city_ids: item?.city_ids || [],
          location_ids: item?.location_ids || [],
        }));
        setFranchiseData(data);
        setSelectedFranchises(prev =>
          prev.filter(f => data.some(d => d.id === f.id))
        );
        setStatePayload(prev => ({
          ...prev,
          franchise_ids: (prev.franchise_ids || []).filter(id =>
            data.some(d => d.id === id)
          ),
        }));
      })
      .catch(err => {});
  };

  const getFilteredFranchises = () => {
    return FranchiseData.filter(franchise => {
      // Check if franchise matches any selected region
      const matchesRegion =
        !statePayload?.region_ids?.length ||
        franchise?.region_ids?.some(regionId =>
          statePayload?.region_ids?.includes(regionId)
        );

      // Check if franchise matches any selected state
      const matchesState =
        !statePayload?.state_ids?.length ||
        franchise?.state_ids?.some(stateId =>
          statePayload?.state_ids?.includes(stateId)
        );

      // Check if franchise matches any selected city
      const matchesCity =
        !statePayload?.city_ids?.length ||
        franchise?.city_ids?.some(cityId =>
          statePayload?.city_ids?.includes(cityId)
        );

      // Check if franchise matches any selected location/area
      const matchesLocation =
        !statePayload?.location_ids?.length ||
        franchise?.location_ids?.some(locationId =>
          statePayload?.location_ids?.includes(locationId)
        );

      // Franchise matches if it matches at least one selected location type
      return matchesRegion || matchesState || matchesCity || matchesLocation;
    });
  };

  const handleSelectAllRegions = () => {
    setSelectedRegions(RegionData);

    let regionIds = RegionData?.map(reg => reg?.id);
    setStatePayload(prev => ({
      ...prev,
      region_ids: regionIds,
    }));
  };
  const handleDeselectAllRegions = () => {
    setSelectedRegions([]);
    setSelectedStates([]);
    setSelectedCities([]);
    setSelectedLocations([]);
    setSelectedFranchises([]);

    setStatePayload(prev => ({
      ...prev,
      region_ids: [],
      state_ids: [],
      city_ids: [],
      location_ids: [],
      franchise_ids: [],
    }));
  };
  const handleChangeRegions = regions => {
    if (!regions?.length) {
      handleDeselectAllRegions();

      return;
    }

    setSelectedRegions(regions);

    let regionIds = regions?.map(reg => reg?.id);

    let tempStates = selectedStates?.filter(state =>
      regionIds?.includes(state?.region_id)
    );
    setSelectedStates(tempStates);

    let stateIds = tempStates?.map(state => state?.id);
    let tempCities = selectedCities?.filter(
      city =>
        regionIds?.includes(city?.region_id) &&
        stateIds?.includes(city?.state_id)
    );
    setSelectedCities(tempCities);

    let cityIds = tempCities?.map(city => city?.id);
    let tempLocations = selectedLocations?.filter(
      loc =>
        regionIds?.includes(loc?.region_id) &&
        stateIds?.includes(loc?.state_id) &&
        cityIds?.includes(loc?.city_id)
    );
    // setSelectedCities(tempLocations);

    let locationIds = tempLocations?.map(loc => loc?.id);

    // Filter selected franchises based on new location selection
    // let filteredFranchises = selectedFranchises?.filter(franchise => {
    //   const franchiseData = FranchiseData.find(f => f?.id === franchise?.id);
    //   if (!franchiseData) return false;
    //   return (
    //     regionIds?.some(id => franchiseData?.region_ids?.includes(id)) ||
    //     stateIds?.some(id => franchiseData?.state_ids?.includes(id)) ||
    //     cityIds?.some(id => franchiseData?.city_ids?.includes(id)) ||
    //     locationIds?.some(id => franchiseData?.location_ids?.includes(id))
    //   );
    // });
    // setSelectedFranchises(filteredFranchises);

    setStatePayload(prev => ({
      ...prev,
      region_ids: regionIds,
      state_ids: stateIds,
      city_ids: cityIds,
      location_ids: locationIds,
      // franchise_ids: filteredFranchises?.map(f => f?.id) || [],
    }));
  };
  console.log("countryData", CountryData);

  const handleSelectAllStates = () => {
    let tempStateData = StateData;

    if (!!statePayload?.region_ids?.length) {
      tempStateData = tempStateData?.filter(state =>
        statePayload?.region_ids?.includes(state?.region_id)
      );
    }

    tempStateData = tempStateData?.filter(
      state => state?.country_id === statePayload?.country_id
    );
    setSelectedStates(tempStateData);

    let stateIds = tempStateData?.map(state => state?.id);

    setStatePayload(prev => ({
      ...prev,
      state_ids: stateIds,
    }));
  };
  const handleDeselectAllStates = () => {
    setSelectedStates([]);
    setSelectedCities([]);
    setSelectedLocations([]);
    // Filter selected franchises to keep only those matching remaining regions
    let filteredFranchises = selectedFranchises?.filter(franchise => {
      const franchiseData = FranchiseData.find(f => f?.id === franchise?.id);
      if (!franchiseData) return false;
      const regionIds = statePayload?.region_ids || [];
      return regionIds?.some(id => franchiseData?.region_ids?.includes(id));
    });
    setSelectedFranchises(filteredFranchises);

    setStatePayload(prev => ({
      ...prev,
      state_ids: [],
      city_ids: [],
      location_ids: [],
      franchise_ids: filteredFranchises?.map(f => f?.id) || [],
    }));
  };
  const handleChangeCountry = e => {
    handleDeselectAllCities();
    handleDeselectAllRegions();
    handleDeselectAllStates();
    handleDeselectAllLocations();
    setStatePayload(prev => ({
      ...prev,
      country_id: parseInt(e.target.value),
    }));
  };
  const handleChangeStates = states => {
    if (!states?.length) {
      handleDeselectAllStates();

      return;
    }

    setSelectedStates(states);

    let stateIds = states?.map(state => state?.id);
    let tempCities = selectedCities?.filter(city =>
      stateIds?.includes(city?.state_id)
    );
    setSelectedCities(tempCities);

    let cityIds = tempCities?.map(city => city?.id);
    let tempLocations = selectedLocations?.filter(
      loc =>
        stateIds?.includes(loc?.state_id) && cityIds?.includes(loc?.city_id)
    );
    setSelectedLocations(tempLocations);

    let locationIds = tempLocations?.map(loc => loc?.id);

    // Filter selected franchises based on new location selection
    // let filteredFranchises = selectedFranchises?.filter(franchise => {
    //   const franchiseData = FranchiseData.find(f => f?.id === franchise?.id);
    //   if (!franchiseData) return false;
    //   const regionIds = statePayload?.region_ids || [];
    //   return (
    //     regionIds?.some(id => franchiseData?.region_ids?.includes(id)) ||
    //     stateIds?.some(id => franchiseData?.state_ids?.includes(id)) ||
    //     cityIds?.some(id => franchiseData?.city_ids?.includes(id)) ||
    //     locationIds?.some(id => franchiseData?.location_ids?.includes(id))
    //   );
    // });
    // setSelectedFranchises(filteredFranchises);

    setStatePayload(prev => ({
      ...prev,
      state_ids: stateIds,
      city_ids: cityIds,
      location_ids: locationIds,
      //franchise_ids: filteredFranchises?.map(f => f?.id) || [],
    }));
  };

  const handleSelectAllCities = () => {
    let tempCityData = CityData;

    if (!!statePayload?.region_ids?.length) {
      tempCityData = tempCityData?.filter(city =>
        statePayload?.region_ids?.includes(city?.region_id)
      );
    }

    tempCityData = tempCityData?.filter(
      city =>
        city?.country_id === statePayload?.country_id &&
        statePayload?.state_ids?.includes(city?.state_id)
    );
    setSelectedCities(tempCityData);

    let cityIds = tempCityData?.map(reg => reg?.id);

    setStatePayload(prev => ({
      ...prev,
      city_ids: cityIds,
    }));
  };
  const handleDeselectAllCities = () => {
    setSelectedCities([]);
    setSelectedLocations([]);
    // Filter selected franchises to keep only those matching remaining regions/states
    let filteredFranchises = selectedFranchises?.filter(franchise => {
      const franchiseData = FranchiseData.find(f => f?.id === franchise?.id);
      if (!franchiseData) return false;
      const regionIds = statePayload?.region_ids || [];
      const stateIds = statePayload?.state_ids || [];
      return (
        regionIds?.some(id => franchiseData?.region_ids?.includes(id)) ||
        stateIds?.some(id => franchiseData?.state_ids?.includes(id))
      );
    });
    setSelectedFranchises(filteredFranchises);

    setStatePayload(prev => ({
      ...prev,
      city_ids: [],
      location_ids: [],
      franchise_ids: filteredFranchises?.map(f => f?.id) || [],
    }));
  };
  const handleChangeCities = cities => {
    if (!cities?.length) {
      handleDeselectAllCities();

      return;
    }

    setSelectedCities(cities);

    let cityIds = cities?.map(city => city?.id);
    let tempLocations = LocationData?.filter(loc =>
      cityIds?.includes(loc?.city_id)
    );
    setSelectedLocations(tempLocations);

    let locationIds = tempLocations?.map(loc => loc?.id);

    // Filter selected franchises based on new location selection
    // let filteredFranchises = selectedFranchises?.filter(franchise => {
    //   const franchiseData = FranchiseData.find(f => f?.id === franchise?.id);
    //   if (!franchiseData) return false;
    //   const regionIds = statePayload?.region_ids || [];
    //   const stateIds = statePayload?.state_ids || [];
    //   return (
    //     regionIds?.some(id => franchiseData?.region_ids?.includes(id)) ||
    //     stateIds?.some(id => franchiseData?.state_ids?.includes(id)) ||
    //     cityIds?.some(id => franchiseData?.city_ids?.includes(id)) ||
    //     locationIds?.some(id => franchiseData?.location_ids?.includes(id))
    //   );
    // });
    // setSelectedFranchises(filteredFranchises);

    setStatePayload(prev => ({
      ...prev,
      city_ids: cityIds,
      location_ids: locationIds,
      //      franchise_ids: filteredFranchises?.map(f => f?.id) || [],
    }));
  };

  const handleSelectAllLocations = () => {
    let tempLocationData = LocationData;

    if (!!statePayload?.region_ids?.length) {
      tempLocationData = tempLocationData?.filter(loc =>
        statePayload?.region_ids?.includes(loc?.region_id)
      );
    }

    tempLocationData = tempLocationData?.filter(
      loc =>
        loc?.country_id === statePayload?.country_id &&
        statePayload?.state_ids?.includes(loc?.state_id) &&
        statePayload?.city_ids?.includes(loc?.city_id)
    );
    setSelectedLocations(tempLocationData);

    let locationIds = tempLocationData?.map(loc => loc?.id);

    setStatePayload(prev => ({
      ...prev,
      location_ids: locationIds,
    }));
  };
  const handleDeselectAllLocations = () => {
    setSelectedLocations([]);
    // Filter selected franchises to keep only those matching remaining regions/states/cities
    let filteredFranchises = selectedFranchises?.filter(franchise => {
      const franchiseData = FranchiseData.find(f => f?.id === franchise?.id);
      if (!franchiseData) return false;
      const regionIds = statePayload?.region_ids || [];
      const stateIds = statePayload?.state_ids || [];
      const cityIds = statePayload?.city_ids || [];
      return (
        regionIds?.some(id => franchiseData?.region_ids?.includes(id)) ||
        stateIds?.some(id => franchiseData?.state_ids?.includes(id)) ||
        cityIds?.some(id => franchiseData?.city_ids?.includes(id))
      );
    });
    setSelectedFranchises(filteredFranchises);

    setStatePayload(prev => ({
      ...prev,
      location_ids: [],
      franchise_ids: filteredFranchises?.map(f => f?.id) || [],
    }));
  };
  const handleChangeLocations = locations => {
    if (!locations?.length) {
      handleDeselectAllLocations();

      return;
    }

    setSelectedLocations(locations);

    let locationIds = locations?.map(loc => loc?.id);

    // Filter selected franchises based on new location selection
    // let filteredFranchises = selectedFranchises?.filter(franchise => {
    //   const franchiseData = FranchiseData.find(f => f?.id === franchise?.id);
    //   if (!franchiseData) return false;
    //   const regionIds = statePayload?.region_ids || [];
    //   const stateIds = statePayload?.state_ids || [];
    //   const cityIds = statePayload?.city_ids || [];
    //   return (
    //     regionIds?.some(id => franchiseData?.region_ids?.includes(id)) ||
    //     stateIds?.some(id => franchiseData?.state_ids?.includes(id)) ||
    //     cityIds?.some(id => franchiseData?.city_ids?.includes(id)) ||
    //     locationIds?.some(id => franchiseData?.location_ids?.includes(id))
    //   );
    // });
    // setSelectedFranchises(filteredFranchises);

    setStatePayload(prev => ({
      ...prev,
      location_ids: locationIds,
      //      franchise_ids: filteredFranchises?.map(f => f?.id) || [],
    }));
  };

  const handleChangeFranchises = franchises => {
    if (!franchises || franchises.length === 0) {
      setSelectedFranchises([]);
      setStatePayload(prev => ({
        ...prev,
        franchise_ids: [], // 👈 IMPORTANT
      }));
      return;
    }
    setSelectedFranchises(franchises);
    let franchiseIds = franchises?.map(franchise => franchise?.id);
    setStatePayload(prev => ({
      ...prev,
      franchise_ids: franchiseIds,
    }));
  };

  // Fetch time slots based on interviewerID
  const fetchInterTimeslot = async interviewer_id => {
    try {
      const formattedDate = moment().format("YYYY-MM-DD");
      const response = await api.get(
        `${process.env.REACT_APP_API_URL}/admin/user/fetch-timeslots/${interviewer_id}?date=${formattedDate}`,
        {}
      );

      const result = response.data;
      const { user } = result;
      const { timeSlotsData } = user;
      settimeSlotsData(timeSlotsData || {});
    } catch (error) {
      console.error("Error fetching time slots:", error);
    }
  };

  statePayload?.userRegions?.map(elem => {
    const found = RegionData.find(region => region.id === elem.id);
    if (found?.label) elem.label = found.label;
    return elem;
  });
  statePayload?.userStates?.map(elem => {
    const found = StateData?.find(state => state.id === elem.id);
    if (found?.label) elem.label = found.label;
    return elem;
  });
  statePayload?.userCities?.map(elem => {
    const found = CityData?.find(city => city.id === elem.id);
    if (found?.label) elem.label = found.label;
    return elem;
  });
  statePayload?.userLocations?.map(elem => {
    const found = LocationData?.find(location => location.id === elem.id);
    if (found?.label) elem.label = found.label;
    return elem;
  });
  statePayload?.userFranchises?.map(elem => {
    const found = FranchiseData?.find(franchise => franchise.id === elem.id);
    if (found?.label) elem.label = found.label;
    return elem;
  });

  useEffect(() => {
    fetchCountryData();
    fetchRoleData();
    fetchUserDetails();
    fetchVendorList();
    fetchInterTimeslot(userId);
    return () => {};
  }, []);

  useEffect(() => {
    if (!isUserLoaded) return; // ⛔ wait until user fully loaded
    console.log(selectedFranchises, "selectedFranchises");
    const franchiseId = selectedFranchises?.length
      ? selectedFranchises.map(f => f.id)
      : null;

    fetchRegionData(statePayload.company_id, franchiseId);
  }, [isUserLoaded, statePayload?.company_id, selectedFranchises]);

  useEffect(() => {
    if (statePayload?.company_id) {
      const franchiseId = selectedFranchises?.length
        ? selectedFranchises.map(f => f.id)
        : null;

      fetchStateData(statePayload.company_id, franchiseId);
    }
  }, [statePayload?.company_id, selectedFranchises]);

  useEffect(() => {
    if (statePayload?.company_id) {
      const franchiseId = selectedFranchises?.length
        ? selectedFranchises.map(f => f.id)
        : null;

      fetchCityData(statePayload.company_id, franchiseId);
    }
  }, [statePayload?.company_id, selectedFranchises]);

  useEffect(() => {
    if (statePayload?.company_id) {
      const franchiseId = selectedFranchises?.length
        ? selectedFranchises.map(f => f.id)
        : null;

      fetchLocationData(statePayload.company_id, franchiseId);
    }
  }, [statePayload?.company_id, selectedFranchises]);

  useEffect(() => {
    if (!!statePayload?.company_id) {
      //fetchRegionData(statePayload?.company_id);
      // fetchStateData(statePayload?.company_id);
      // fetchCityData(statePayload?.company_id);
      // fetchLocationData(statePayload?.company_id);
      fetchFranchiseData(statePayload?.company_id);
    }

    return () => {};
  }, [statePayload?.company_id]);

  const [activeTab, setActiveTab] = useState("Personal"); // Default tab

  const handleTabClick = tabName => {
    setActiveTab(tabName);
  };

  const handleVendorSelection = vendor_id => {
    setStatePayload(prevState => ({
      ...prevState,
      vendor_id: vendor_id,
    }));
  };
  useEffect(() => {
    const theme = localStorage.getItem("company_theme");
    if (theme) {
      document.body.classList.add(`theme-${theme}`);
    }
  }, []);

  if (!isAdminUser && !isSelfUser) {
    return (
      <div className="page-content">
        <h4 className="text-center">{t(`Edit User.Access Denied`)}</h4>
      </div>
    );
  }

  return (
    <div className="page-content">
      <ul className="nav nav-tabs  edituser-tabs">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "Personal" ? "active" : ""}`}
            onClick={() => handleTabClick("Personal")}
          >
            {t(`Edit User.Personal Details`)}
          </button>
        </li>
        {getSubdomain() !== "mahindra" && (
          <>
            <li className="nav-item">
              <button
                className={`nav-link ${
                  activeTab === "booking" ? "active" : ""
                }`}
                onClick={() => handleTabClick("booking")}
              >
                {t(`Edit User.Timeslot Booking`)}
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${
                  activeTab === "calendar" ? "active" : ""
                }`}
                onClick={() => handleTabClick("calendar")}
              >
                {t(`Edit User.My Calender`)}
              </button>
            </li>
          </>
        )}
      </ul>

      <div className="outerTab-box-wrapper below-vacancy">
        {checkPermissionStatus("user-list", "create") && (
          <div className="joblist-container create-vacancy pe-0 ps-0">
            <Row className="mb-3 align-items-center py-2 pt-0 depart-content">
              <Col lg="6" md="6">
                <Link to="/user-list">
                  <h5 className="text-uppercase heading-one">
                    <img src={arrow_btn} alt="arrow_btn" />
                    {t(`Edit User.User`)}
                  </h5>
                </Link>
              </Col>
            </Row>
          </div>
        )}

        <div className="form-details-section">
          <Container fluid>
            {/* <div className="container"> */}
            <div className="tab-content">
              {activeTab === "Personal" && (
                <div className="tab-pane active">
                  <div className="col-lg-12">
                    <h2 className="sub-heading-text mb-3">
                      {t(`Edit User.Personal Details`)}:
                    </h2>
                    <Row>
                      <Form.Group className="col-md-6 col-lg-4 mb-3 floating-label">
                        <Form.Label className="float-label">
                          {t(`Edit User.First Name`)}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder=" "
                          name="firstname"
                          value={statePayload?.firstname}
                          onChange={handleInput}
                          title={`${t(`Edit User.First Name`)}`}
                        />
                        {errors?.firstname && (
                          <small className="text-danger">
                            {" "}
                            {errors?.firstname}{" "}
                          </small>
                        )}
                      </Form.Group>

                      <Form.Group className="col-md-6 col-lg-4 mb-3 floating-label">
                        <Form.Label className="float-label">
                          {t(`Edit User.Last Name`)}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder=" "
                          name="lastname"
                          value={statePayload?.lastname}
                          onChange={handleInput}
                          title={`${t(`Edit User.Last Name`)}`}
                        />
                        {errors?.lastname && (
                          <small className="text-danger">
                            {" "}
                            {errors?.lastname}{" "}
                          </small>
                        )}
                      </Form.Group>

                      <Form.Group className="col-md-6 col-lg-4 mb-3 floating-label">
                        <Form.Label className="float-label">
                          {t(`Edit User.Email`)}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder=" "
                          name="email"
                          value={
                            isSelfUser || isAdminUser
                              ? statePayload?.email
                              : generateMaskedPII(statePayload?.email)
                          }
                          onChange={handleInput}
                          title={`${t(`Edit User.Email`)}`}
                          disabled={
                            !isSelfUser &&
                            !isAdminUser &&
                            !checkPermissionStatus(
                              "personal-id-info",
                              "view_unmasked"
                            )
                          }
                        />
                        {errors?.email && (
                          <small className="text-danger">
                            {" "}
                            {errors?.email}{" "}
                          </small>
                        )}
                      </Form.Group>

                      <Form.Group className="col-md-6 col-lg-4 mb-3 floating-label">
                        <Form.Label className="float-label">
                          {t(`Edit User.Mobile No`)}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder={getPhonePlaceholder(
                            statePayload?.country_id
                          )}
                          name="phone"
                          value={
                            isSelfUser || isAdminUser
                              ? statePayload?.phone
                              : generateMaskedPII(statePayload?.phone, 2)
                          }
                          onChange={handleInput}
                          title={`${t(`Edit User.Mobile No`)}`}
                          maxLength={getPhoneMaxLength(
                            statePayload?.country_id
                          )}
                          disabled={
                            !isSelfUser &&
                            !isAdminUser &&
                            !checkPermissionStatus(
                              "personal-id-info",
                              "view_unmasked"
                            )
                          }
                        />
                        {errors?.phone && (
                          <small className="text-danger">
                            {" "}
                            {errors?.phone}{" "}
                          </small>
                        )}
                      </Form.Group>

                      <Form.Group className="col-md-6 col-lg-4 mb-3 floating-label">
                        <Form.Label className="float-label">
                          {t(`Edit User.Image`)}
                          <span className="text-danger"></span>
                        </Form.Label>
                        <Form.Control
                          type="file"
                          className="form-control"
                          id="image"
                          name="image"
                          accept=".jpeg, .png, .jpg, .gif"
                          errorMessage="The file is required."
                          onChange={handleImageFileChange}
                          title={`${t(`Edit User.Image`)}`}
                        />
                        {errors?.phone && (
                          <small className="text-danger">
                            {" "}
                            {errors?.image}{" "}
                          </small>
                        )}
                      </Form.Group>

                      {statePayload.image && (
                        <>
                          <div>
                            <img
                              src={statePayload.image}
                              alt="Selected Image"
                              style={{ maxWidth: "20%" }}
                            />
                          </div>
                        </>
                      )}
                    </Row>
                    <Row>
                      {/* <Form.Group className="col-md-12 mb-3 floating-label">
                        <Form.Check
                          id="vendor-switch" // Changed to a unique id to ensure no conflicts
                          name="vendor"
                          type="switch"
                          className="h6"
                          label={t(`Create User.Is Vendor`)} // Proper label text
                          title={t(
                            `Please check ${`Create User.Is Vendor`.toLowerCase()}`
                          )} // Proper title for accessibility
                          checked={isVendorVisible}
                          onChange={e => {
                            const isChecked = e.target.checked;
                            setIsVendorVisible(isChecked);
                            if (!isChecked) {
                              setStatePayload(prevState => ({
                                ...prevState,
                                vendor_id: 0,
                              }));
                            }
                          }}
                        />
                      </Form.Group> */}

                      {isVendorVisible && (
                        <Row>
                          <Form.Group className="col-md-4 col-sm-6 mb-3 floating-label">
                            <Form.Label className="float-label">
                              {t(`Create User.Vendor`)}
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <select
                              type="select"
                              name="vendor_id"
                              className="col-md-4 mb-1 form-control form-select"
                              style={{ width: "100%" }}
                              onChange={e =>
                                handleVendorSelection(e.target?.value)
                              }
                              value={statePayload?.vendor_id}
                              onMouseEnter={e => {
                                e.target.setAttribute(
                                  "title",
                                  e.target.options[e.target.selectedIndex].text
                                );
                              }}
                            >
                              <option value="" selected>
                                {t(`Create User.Select Vendor`)}
                              </option>

                              {vendor &&
                                vendor.map(el => (
                                  <option key={el?.value} value={el?.id}>
                                    {el?.label}
                                  </option>
                                ))}
                            </select>

                            {errors?.vendor_id && (
                              <small className="text-danger">
                                {" "}
                                {errors?.vendor_id}{" "}
                              </small>
                            )}
                          </Form.Group>
                        </Row>
                      )}
                    </Row>
                    <br />

                    <h2 className="sub-heading-text mb-3">
                      {t(`Edit User.Login Details`)}:
                    </h2>

                    <Row>
                      <Form.Group className="col-md-4 col-sm-6 mb-3 floating-label">
                        <Form.Label className="float-label">
                          {t(`Edit User.Country`)}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <select
                          type="select"
                          name="country_id"
                          className="col-md-4 mb-1 form-control form-select"
                          style={{ width: "100%" }}
                          value={statePayload?.country_id}
                          onChange={handleChangeCountry}
                          disabled={statePayload.country_id ? true : false}
                          title={
                            CountryData.find(
                              el => el?.id === statePayload?.country_id
                            )?.label || t(`Create User.Country`)
                          }
                        >
                          <option key={0} value={0}>
                            {t(`Edit User.Select Country`)}
                          </option>
                          {CountryData &&
                            CountryData.map(el => (
                              <option key={el?.value} value={el?.id}>
                                {el?.label}
                              </option>
                            ))}
                        </select>

                        {errors?.country_id && (
                          <small className="text-danger">
                            {" "}
                            {errors?.country_id}{" "}
                          </small>
                        )}
                      </Form.Group>
                      <Form.Group className="col-md-4 col-sm-6 mb-3 floating-label">
                        <Form.Label className="float-label">
                          {"Dealership"}
                        </Form.Label>

                        <div
                          title={
                            selectedFranchises.length
                              ? selectedFranchises
                                  .map(option => option?.label || option?.name)
                                  .filter(Boolean)
                                  .join(", ")
                              : "Dealership"
                          }
                        >
                          <MultiSelect
                            options={FranchiseData}
                            value={selectedFranchises}
                            onChange={handleChangeFranchises}
                            overrideStrings={{
                              selectSomeItems: "Select Dealership",
                            }}
                            disabled={false}
                          />
                        </div>
                      </Form.Group>
                      <Form.Group className="col-md-4 col-sm-6 mb-3 floating-label">
                        <Form.Label className="float-label">
                          {t(`Edit User.Region`)}
                        </Form.Label>

                        <div
                          title={
                            selectedRegions.length
                              ? `${selectedRegions
                                  .map(option => option.label || option.name)
                                  .join(", ")}`
                              : `${t("Create User.Region")}`
                          }
                        >
                          <MultiSelect
                            // isMulti
                            options={[
                              // {
                              //   isDisabled: true,
                              //   value: null,
                              //   label: (
                              //     <div className="d-flex gap-2">
                              //       <button
                              //         className="selectall-btn"
                              //         onClick={handleSelectAllRegions}
                              //       >
                              //         Select All
                              //       </button>
                              //       <button
                              //         className="selectall-btn"
                              //         onClick={handleDeselectAllRegions}
                              //       >
                              //         Deselect All
                              //       </button>
                              //     </div>
                              //   ),
                              // },
                              ...RegionData?.filter(
                                region =>
                                  !statePayload?.country_id ||
                                  region?.country_id ===
                                    statePayload?.country_id
                              ),
                            ]}
                            value={selectedRegions}
                            onChange={handleChangeRegions}
                            overrideStrings={{
                              selectSomeItems: t(`Edit User.Select Region`),
                            }}
                            disabled={false}
                          />
                        </div>
                      </Form.Group>
                      <Form.Group className="col-md-4 col-sm-6 mb-3 floating-label">
                        <Form.Label className="float-label">
                          {t(`Edit User.State`)}
                        </Form.Label>

                        <div
                          title={
                            selectedStates.length
                              ? `${selectedStates
                                  .map(option => option.label || option.name)
                                  .join(", ")}`
                              : `${t("Create User.State")}`
                          }
                        >
                          <MultiSelect
                            // isMulti
                            options={[
                              // {
                              //   isDisabled: true,
                              //   value: null,
                              //   label: (
                              //     <div className="d-flex gap-2">
                              //       <button
                              //         className="selectall-btn"
                              //         onClick={handleSelectAllStates}
                              //       >
                              //         Select All
                              //       </button>
                              //       <button
                              //         className="selectall-btn"
                              //         onClick={handleDeselectAllStates}
                              //       >
                              //         Deselect All
                              //       </button>
                              //     </div>
                              //   ),
                              // },
                              ...StateData?.filter(
                                state =>
                                  !statePayload?.country_id ||
                                  (!statePayload?.region_ids?.length &&
                                    state?.country_id ===
                                      statePayload?.country_id) ||
                                  (state?.country_id ===
                                    statePayload?.country_id &&
                                    statePayload?.region_ids?.includes(
                                      state?.region_id
                                    ))
                              ),
                            ]}
                            value={selectedStates}
                            onChange={handleChangeStates}
                            overrideStrings={{
                              selectSomeItems: t(`Edit User.Select State`),
                            }}
                            disabled={false}
                          />
                        </div>
                      </Form.Group>
                      <Form.Group className="col-md-4 col-sm-6 mb-3 floating-label">
                        <Form.Label className="float-label">
                          {t(`Edit User.City`)}
                        </Form.Label>

                        <div
                          title={
                            selectedCities.length
                              ? `${selectedCities
                                  .map(option => option.label || option.name)
                                  .join(", ")}`
                              : `${t("Create User.City")}`
                          }
                        >
                          <MultiSelect
                            // isMulti
                            options={[
                              // {
                              //   isDisabled: true,
                              //   value: null,
                              //   label: (
                              //     <div className="d-flex gap-2">
                              //       <button
                              //         className="selectall-btn"
                              //         onClick={handleSelectAllCities}
                              //       >
                              //         Select All
                              //       </button>
                              //       <button
                              //         className="selectall-btn"
                              //         onClick={handleDeselectAllCities}
                              //       >
                              //         Deselect All
                              //       </button>
                              //     </div>
                              //   ),
                              // },
                              ...CityData?.filter(
                                city =>
                                  !statePayload?.country_id ||
                                  (!statePayload?.region_ids?.length &&
                                    city?.country_id ===
                                      statePayload?.country_id &&
                                    statePayload?.state_ids?.includes(
                                      city?.state_id
                                    )) ||
                                  (city?.country_id ===
                                    statePayload?.country_id &&
                                    statePayload?.region_ids?.includes(
                                      city?.region_id
                                    ) &&
                                    statePayload?.state_ids?.includes(
                                      city?.state_id
                                    ))
                              ),
                            ]}
                            value={selectedCities}
                            onChange={handleChangeCities}
                            overrideStrings={{
                              selectSomeItems: t(`Edit User.Select City`),
                            }}
                            aria-label={t(`Edit User.City`)} // Adds an ARIA label
                            tabIndex="0"
                            disabled={false}
                          />
                        </div>
                      </Form.Group>
                      <Form.Group className="col-md-4 col-sm-6 mb-3 floating-label">
                        <Form.Label className="float-label">
                          {t(`Edit User.Area`)}
                        </Form.Label>

                        <div
                          title={
                            selectedLocations.length
                              ? `${selectedLocations
                                  .map(option => option.label || option.name)
                                  .join(", ")}`
                              : `${t("Create User.Area")}`
                          }
                        >
                          <MultiSelect
                            // isMulti
                            options={[
                              ...LocationData?.filter(
                                loc =>
                                  !statePayload?.country_id ||
                                  (!statePayload?.region_ids?.length &&
                                    loc?.country_id ===
                                      statePayload?.country_id &&
                                    statePayload?.state_ids?.includes(
                                      loc?.state_id
                                    ) &&
                                    statePayload?.city_ids?.includes(
                                      loc?.city_id
                                    )) ||
                                  (loc?.country_id ===
                                    statePayload?.country_id &&
                                    statePayload?.region_ids?.includes(
                                      loc?.region_id
                                    ) &&
                                    statePayload?.state_ids?.includes(
                                      loc?.state_id
                                    ) &&
                                    statePayload?.city_ids?.includes(
                                      loc?.city_id
                                    ))
                              ),
                            ]}
                            value={selectedLocations}
                            onChange={handleChangeLocations}
                            overrideStrings={{
                              selectSomeItems: t(`Edit User.Select Area`),
                            }}
                            aria-label={t(`Edit User.Select Area`)} // Adds an ARIA label
                            tabIndex="0"
                            disabled={false}
                          />
                        </div>
                      </Form.Group>

                      <Form.Group className="col-md-4 mb-3">
                        <Form.Label>{t(`Edit User.Select Role`)}*</Form.Label>
                        <div
                          title={
                            statePayload.role
                              ? `${
                                  roles.find(
                                    elem => elem.value == statePayload.role
                                  )?.label
                                }`
                              : `${t(`EmployeeReferral.Status`)}`
                          }
                        >
                          <Select
                            id="select"
                            placeholder={t(`Edit User.Select`)}
                            className="col-md-12"
                            options={roles}
                            value={fetchSelectValue(statePayload?.role)}
                            onChange={e => handleSelectChange(e, "role")}
                            styles={styles}
                            aria-label={t(`Edit User.Select Role`)} // Adds an ARIA label
                            tabIndex="0"
                            isDisabled={true}
                          />
                        </div>
                        {errors?.role && (
                          <small className="text-danger">
                            {" "}
                            {errors?.role}{" "}
                          </small>
                        )}
                      </Form.Group>

                      <Form.Group className="col-md-4 mb-3 floating-label">
                        <Form.Label className="float-label">
                          {t(`Edit User.Username`)}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          autocomplete="off"
                          placeholder=" "
                          name="username"
                          value={statePayload?.username}
                          onChange={handleInput}
                          title={`${t(`Edit User.Username`)}`}
                          disabled={true}
                        />
                        {errors?.username && (
                          <small className="text-danger">
                            {" "}
                            {errors?.username}{" "}
                          </small>
                        )}
                      </Form.Group>

                      <Form.Group className="col-md-12 mb-3 floating-label">
                        <Form.Check
                          id="change-pwd" // Ensure the id is unique
                          name="change-pwd"
                          autocomplete="off"
                          type="switch"
                          className="h6"
                          label={"Change Password"} // This will be the label displayed next to the switch
                          checked={statePayload?.change_password}
                          onChange={e => {
                            setStatePayload(prevState => ({
                              ...prevState,
                              change_password: !statePayload?.change_password,
                            }));
                          }}
                          title="Please check to change password" // Fix syntax error here
                        />
                      </Form.Group>

                      <Form.Group className="col-md-4 mb-3 floating-label">
                        <Form.Label className="float-label">
                          {t(`Edit User.Password`)}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="password"
                          placeholder=" "
                          disabled={!statePayload?.change_password}
                          name="password"
                          value={statePayload?.password}
                          onChange={handleInput}
                          title={`${t(`Edit User.Password`)}`}
                        />
                        {errors?.password && (
                          <small className="text-danger">
                            {" "}
                            {errors?.password}{" "}
                          </small>
                        )}
                      </Form.Group>

                      <Form.Group className="col-md-4 mb-3 floating-label">
                        <Form.Label className="float-label">
                          {t(`Edit User.Confirm Password`)}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="password"
                          placeholder=" "
                          disabled={!statePayload?.change_password}
                          name="confirm_password"
                          value={statePayload?.confirm_password}
                          onChange={handleInput}
                          title={`${t(`Edit User.Confirm Password`)}`}
                        />
                        {errors?.confirm_password && (
                          <small className="text-danger">
                            {" "}
                            {errors?.confirm_password}{" "}
                          </small>
                        )}
                      </Form.Group>

                      <Form.Group className="cta text-center gap-3 d-flex justify-content-left mt-4 mb-4">
                        <Link
                          to={
                            checkPermissionStatus("user-list", "view")
                              ? "/user-list"
                              : "/dashboard"
                          }
                          style={{ marginLeft: "5px" }}
                          className="btn btn-secondary"
                        >
                          {t(`Edit User.Cancel`)}
                        </Link>
                        <ButtonLoader
                          title={t(`Edit User.Save Details`)}
                          variant="primary"
                          style={{ marginLeft: "5px" }}
                          type="submit"
                          loading={isLoading}
                          message={"Saving"}
                          onClick={e => {
                            handleSubmit(e);
                          }}
                        />
                      </Form.Group>
                    </Row>
                  </div>
                </div>
              )}

              {activeTab === "booking" && (
                <div className="tab-pane active">
                  <div className="col-lg-12">
                    <TimeslotBooking
                      onValueChange={handleChildValueChange}
                      defaultTimeSlots={timeSlotsData || {}}
                      onErrorHandle={handleErrorsValueChange || {}}
                    />
                  </div>
                </div>
              )}

              {activeTab === "calendar" && (
                <div className="tab-pane active">
                  <CalendarPreview timeslots={timeslots} />
                </div>
              )}
            </div>
            {/* </div> */}
          </Container>
        </div>
      </div>
    </div>
  );
};

export default EditUser;
